/**
 * DeviceListPage — main KDS operator view.
 *
 * Lists paired devices for the active merchant. Empty state on
 * first-run (linked from setup-portal task) shows guided "pair your
 * first tablet" copy.
 *
 * The list is fetched live via `api.listDevices` → kds-api's
 * session-validated GET /api/v1/admin/m/{mid}/devices (requireMerchantSession),
 * which returns the merchant's paired devices; AuthGate uses the same
 * endpoint as its boot auth probe. (The earlier "no session-validating
 * merchant API exists yet" note was stale — that endpoint shipped with
 * the C.1 merchant-admin routes.)
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { PRODUCTS } from '@tovriktech/registry';
import { Button, Card, EmptyState, Skeleton, StatusBadge } from '@tovriktech/ui';
import { api, type Device } from '../lib/api';

// A device is "online" if it authenticated within the last 5 minutes; the list
// already excludes revoked devices, so every displayed row is paired — the
// StatusBadge distinguishes recently-active (connected) from idle (inactive).
const ONLINE_WINDOW_MS = 5 * 60_000;

function isOnline(lastSeenIso: string | null | undefined): boolean {
  if (!lastSeenIso) return false;
  const t = new Date(lastSeenIso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < ONLINE_WINDOW_MS;
}

/** A currentColor tablet/device glyph — inherits the EmptyState teal medallion. */
function DeviceIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2.5" width="16" height="19" rx="2.5" />
      <line x1="10.5" y1="18.5" x2="13.5" y2="18.5" />
    </svg>
  );
}

export interface DeviceListPageProps {
  merchantId: string;
  firstRun: boolean;
  onPairNew: () => void;
  onOpenMonitor: () => void;
}

export function DeviceListPage(props: DeviceListPageProps): ReactNode {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api.listDevices(props.merchantId);
      // Hide revoked devices from the main list — they're still in the
      // table for audit but not actively paired.
      setDevices(r.devices.filter((d) => !d.revoked_at));
    } catch (e) {
      // 401 is the AuthGate's job (it mounts above this route and
      // redirects to /auth/sign-in on boot-probe failure). Mid-session
      // 401s here surface as a load-error banner; the operator can
      // refresh the page to re-trigger the AuthGate probe.
      setLoadError(e instanceof Error ? e.message : String(e));
      setDevices([]);
    }
  }, [props.merchantId]);

  useEffect(() => {
    setDevices(null);
    void refresh();
  }, [refresh]);

  async function onRevoke(d: Device) {
    if (!confirm(`Revoke ${d.name}? The tablet will lose access and need to re-pair.`)) return;
    setRevokingId(d.id);
    try {
      await api.revokeDevice(props.merchantId, d.id, 'revoked from admin');
      await refresh();
    } catch (e) {
      alert(`Revoke failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRevokingId(null);
    }
  }

  const empty = devices !== null && devices.length === 0;

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-tvk-serif text-2xl font-semibold tracking-tight text-tvk-fg">{PRODUCTS.kitchen.display} Devices</h1>
          <p className="text-sm text-tvk-fg-muted">
            Manage the tablets paired to this kitchen — pair a new station, watch live activity, or revoke access.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <Button type="button" variant="secondary" size="sm" onClick={props.onOpenMonitor}>
            Live monitor
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={props.onPairNew}>
            + Pair new device
          </Button>
        </div>
      </div>

      {props.firstRun && empty && (
        <FirstRunGuide onPairNew={props.onPairNew} />
      )}

      {!props.firstRun && empty && (
        <Card className="mt-8" padding="lg">
          <EmptyState
            icon={<DeviceIcon />}
            title="No paired devices yet"
            description={`Pair your first ${PRODUCTS.kitchen.display} tablet to start displaying tickets.`}
            action={
              <Button type="button" variant="primary" size="sm" onClick={props.onPairNew}>
                Pair first device →
              </Button>
            }
          />
        </Card>
      )}

      {loadError && (
        <div className="mt-6 rounded-tvk-md border border-tvk-danger/30 bg-tvk-danger/10 p-3 text-xs text-tvk-danger">
          Failed to load devices: {loadError}
        </div>
      )}

      {devices === null && (
        <Card className="mt-6 overflow-hidden" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading devices…</span>
          <ul className="divide-y divide-tvk-border">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton height={14} width="40%" />
                  <Skeleton height={11} width="65%" />
                </div>
                <Skeleton height={24} width={64} shape="rect" />
                <Skeleton height={32} width={84} shape="rect" />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {devices && devices.length > 0 && (
        <Card className="mt-6 overflow-hidden">
          <ul className="divide-y divide-tvk-border">
            {devices.map((d) => {
              const online = isOnline(d.last_seen_at);
              return (
                <li
                  key={d.id}
                  className="group flex items-center justify-between gap-4 px-5 py-3.5 transition-colors duration-150 ease-tvk-smooth hover:bg-tvk-surface-muted/40"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span className="truncate text-sm font-medium text-tvk-fg">{d.name}</span>
                      <StatusBadge status={online ? 'connected' : 'inactive'} className="flex-shrink-0">
                        {online ? 'Online' : 'Idle'}
                      </StatusBadge>
                    </div>
                    <div className="mt-0.5 text-xs text-tvk-fg-muted">
                      Last seen {formatRelative(d.last_seen_at)} · {d.device_type}
                      {' · '}
                      <span className="font-mono tabular-nums">{d.api_key_prefix}</span>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void onRevoke(d)}
                      disabled={revokingId === d.id}
                    >
                      {revokingId === d.id ? 'Revoking…' : 'Revoke'}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <p className="mt-6 text-xs text-tvk-fg-muted">
        Merchant: <span className="font-mono">{props.merchantId}</span>
      </p>
    </div>
  );
}

function FirstRunGuide({ onPairNew }: { onPairNew: () => void }) {
  // First-run highlight: a soft terracotta-accent tinted panel with DARK
  // text (text-tvk-fg) — never white-on-terracotta (fails AA). The CTA
  // is the primary teal Button (white-on-teal, AAA).
  return (
    <div className="mt-8 rounded-tvk-lg border border-tvk-accent/30 bg-tvk-accent/10 p-6">
      <div className="font-tvk-serif text-base font-semibold text-tvk-fg">Pair your first {PRODUCTS.kitchen.display} tablet</div>
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-tvk-fg">
        <li>
          Power up the tablet at the kitchen station and open the {PRODUCTS.kitchen.display} app.
        </li>
        <li>
          The tablet will display an 8-character pairing code like{' '}
          <code className="rounded bg-tvk-surface px-1 py-0.5 text-xs">ABCD-1234</code>.
        </li>
        <li>
          Click <strong>Pair first device</strong> below and type the code shown.
        </li>
      </ol>
      <Button type="button" variant="primary" onClick={onPairNew} className="mt-5">
        Pair first device →
      </Button>
    </div>
  );
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return iso;
  }
}
